import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  MapPinIcon,
  DocumentTextIcon,
  PaperClipIcon,
  ChatBubbleLeftIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RichTextEditor, RichTextDisplay } from '@/components/ui/RichTextEditor';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { cn } from '@/utils/cn';
import { toDateTimeLocalValue } from '@/utils/datetime';
import { canMarkIncidentResolved } from '@/utils/incidentStatus';
import { incidentsService, Incident, UpdateIncidentData, IncidentHistory } from '../services/incidents.service';
import { DOCUMENT_FILE_EXTENSIONS, FILE_SIZE_LIMITS, FILE_UPLOAD_ERRORS } from '@/constants/file-upload';
import { usersService, User } from '../services/users.service';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog';
import ReassignModal from '@/components/incidents/ReassignModal';
import ChangePriorityModal from '@/components/incidents/ChangePriorityModal';
import EscalateModal, { EscalateData } from '@/components/incidents/EscalateModal';
import LinkSearchModal from '@/components/common/LinkSearchModal';
import CreateInvestigationFromIncidentModal from '@/components/incidents/CreateInvestigationFromIncidentModal';
import { RequestClosureModal } from '@/components/incidents/RequestClosureModal';
import { ApproveClosureModal } from '@/components/incidents/ApproveClosureModal';
import { RejectClosureModal } from '@/components/incidents/RejectClosureModal';
import { UserPicker } from '@/components/common/UserPicker';
import { useAuthStore } from '@/stores/auth';
import { LinkedSubjectsPanel } from '@/components/subjects/LinkedSubjectsPanel';
import { XMarkIcon as XMarkSmallIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { useAnyRole } from '@/hooks/useRole';
import apiClient from '@/services/api';
import { SECURITY_CATEGORIES } from '@corporate-security/types';

interface TimelineEvent {
  id: string;
  action: string;
  details: string;
  timestamp: string;
  actor: string;
  type: 'created' | 'updated' | 'comment' | 'status_change' | 'assignment' | 'attachment';
}

export default function IncidentDetails() {
  const params = useParams<{ id: string }>();
  const id = params.id ?? '';
  const navigate = useNavigate();
  const location = useLocation();
  const { setOverride, clearOverride } = useBreadcrumb();
  const canEditIncidentNumber = useAnyRole(['SUPER_ADMIN', 'ADMIN', 'SECURITY_MANAGER']);
  const [incident, setIncident] = useState<Incident | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);

  // Form state for editable fields
  const [editForm, setEditForm] = useState({
    description: '',
    location: '',
    category: '',
    severity: 'MODERATE' as 'MINIMAL' | 'MODERATE' | 'HIGH',
    affectedSystems: [] as string[],
    tags: [] as string[],
    assigneeId: '',
    occurredAt: '',
    incidentNumber: '',
    supervisorId: '',
    supervisorName: '',
    supervisorEmail: '',
  });
  const [newSystem, setNewSystem] = useState('');
  const [newTag, setNewTag] = useState('');
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Users state for assignee dropdown
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Assignee user data (fetched separately from incident)
  const [assigneeUser, setAssigneeUser] = useState<User | null>(null);

  // Quick Actions modal state
  const [showResolveConfirm, setShowResolveConfirm] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);

  // Closure workflow modal state
  const [showRequestClosureModal, setShowRequestClosureModal] = useState(false);
  const [showApproveClosureModal, setShowApproveClosureModal] = useState(false);
  const [showRejectClosureModal, setShowRejectClosureModal] = useState(false);

  // Linked Investigations state
  const [showLinkInvestigationModal, setShowLinkInvestigationModal] = useState(false);
  const [linkedInvestigations, setLinkedInvestigations] = useState<Array<{ id: string; caseNumber: string; title: string; status: string }>>([]);

  // Create Investigation from Incident feature (VIS-23 — flag removed, always show per permission)
  const canCreateInvestigation = useAnyRole([
    'SUPER_ADMIN',
    'ADMIN',
    'SECURITY_MANAGER',
    'ANALYST',
  ]);
  const [showCreateInvestigationModal, setShowCreateInvestigationModal] = useState(false);

  const status = (incident?.status ?? '').toUpperCase();
  const incidentClosed = ['CLOSED', 'CANCELLED'].includes(status);
  const investigationCreationBlocked = ['CLOSED', 'CANCELLED', 'RESOLVED'].includes(status);
  const incidentPending = status === 'PENDING_CLOSURE_APPROVAL';
  const incidentActive = ['OPEN', 'IN_PROGRESS', 'RESOLVED'].includes(status);
  // "Mark as Resolved" is only a legal FSM transition from OPEN / IN_PROGRESS;
  // offering it elsewhere (CANCELLED, CLOSED, PENDING_CLOSURE_APPROVAL, RESOLVED)
  // produces a 400 INVALID_STATE_TRANSITION. Keep the button gate in sync.
  const canResolve = canMarkIncidentResolved(status);

  // Current user for closure workflow role checks
  const currentUserId = useAuthStore((state) => state.user?.id ?? '');
  const isSupervisor = !!incident && !!currentUserId && incident.supervisorId === currentUserId;
  const isRequester = !!incident && !!currentUserId && incident.closureRequestedBy === currentUserId;

  const severityColors = {
    high: 'bg-danger-100 text-danger-800 border-danger-200 dark:bg-danger-900/20 dark:text-danger-300 dark:border-danger-800',
    moderate: 'bg-warning-100 text-warning-800 border-warning-200 dark:bg-warning-900/20 dark:text-warning-300 dark:border-warning-800',
    minimal: 'bg-success-100 text-success-800 border-success-200 dark:bg-success-900/20 dark:text-success-300 dark:border-success-800',
  };

  const statusColors = {
    open: 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300',
    in_progress: 'bg-warning-100 text-warning-800 dark:bg-warning-900/20 dark:text-warning-300',
    resolved: 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300',
    closed: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-800 dark:text-secondary-300',
    pending_closure_approval: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
    cancelled: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-400',
  };

  const timelineIcons = {
    created: ExclamationTriangleIcon,
    updated: PencilIcon,
    comment: ChatBubbleLeftIcon,
    status_change: ClockIcon,
    assignment: UserIcon,
    attachment: PaperClipIcon,
  };

  // Field name mapping for display
  const fieldNameLabels: Record<string, string> = {
    title: 'Title',
    description: 'Description',
    severity: 'Severity',
    status: 'Status',
    priority: 'Priority',
    category: 'Category',
    location: 'Location',
    affectedSystems: 'Affected Systems',
    tags: 'Tags',
    assignedTo: 'Assigned To',
    reporterName: 'Reporter Name',
    reporterEmail: 'Reporter Email',
    reporterPhone: 'Reporter Phone',
    occurredAt: 'Occurred Date',
    incidentNumber: 'Incident Number',
  };

  // Build timeline from multiple data sources
  const buildTimeline = (incident: Incident): TimelineEvent[] => {
    const events: TimelineEvent[] = [];

    // 1. Add incident creation event
    events.push({
      id: `created-${incident.id}`,
      action: 'Incident Created',
      details: incident.title,
      timestamp: incident.createdAt,
      actor: incident.reporterName || 'System',
      type: 'created',
    });

    // 2. Add history events (field changes)
    if (incident.history && incident.history.length > 0) {
      // Group history by timestamp to combine multiple field changes
      const historyByTimestamp = new Map<string, IncidentHistory[]>();
      incident.history.forEach(h => {
        const key = h.changedAt;
        const existingEvents = historyByTimestamp.get(key);
        if (existingEvents) {
          existingEvents.push(h);
        } else {
          historyByTimestamp.set(key, [h]);
        }
      });

      // Create timeline events from grouped history
      historyByTimestamp.forEach((changes, timestamp) => {
        if (changes.length === 1) {
          const change = changes[0];
          const fieldLabel = fieldNameLabels[change.fieldName] ?? change.fieldName;
          const isStatusChange = change.fieldName === 'status';
          const isAssignment = change.fieldName === 'assignedTo';

          events.push({
            id: change.id,
            action: isStatusChange
              ? 'Status Updated'
              : isAssignment
                ? 'Reassigned'
                : `${fieldLabel} Updated`,
            details: change.oldValue && change.newValue
              ? `Changed from ${change.oldValue} to ${change.newValue}`
              : change.newValue
                ? `Set to ${change.newValue}`
                : `Cleared ${fieldLabel.toLowerCase()}`,
            timestamp: change.changedAt,
            actor: change.changedBy === 'system' ? 'System' : change.changedBy,
            type: isStatusChange ? 'status_change' : isAssignment ? 'assignment' : 'updated',
          });
        } else {
          // Multiple fields changed at once
          const fieldsList = changes.map(c => fieldNameLabels[c.fieldName] ?? c.fieldName).join(', ');
          events.push({
            id: changes[0].id,
            action: 'Multiple Fields Updated',
            details: `Updated: ${fieldsList}`,
            timestamp: timestamp,
            actor: changes[0].changedBy === 'system' ? 'System' : changes[0].changedBy,
            type: 'updated',
          });
        }
      });
    }

    // 3. Add comment events
    if (incident.comments && incident.comments.length > 0) {
      incident.comments.forEach(comment => {
        events.push({
          id: `comment-${comment.id}`,
          action: 'Comment Added',
          details: comment.comment.length > 100
            ? comment.comment.substring(0, 100) + '...'
            : comment.comment,
          timestamp: comment.createdAt,
          actor: comment.userId === 'system' ? 'System' : comment.userId,
          type: 'comment',
        });
      });
    }

    // 4. Add attachment events
    if (incident.attachments && incident.attachments.length > 0) {
      incident.attachments.forEach(attachment => {
        events.push({
          id: `attachment-${attachment.id}`,
          action: 'Attachment Uploaded',
          details: attachment.originalFilename,
          timestamp: attachment.uploadedAt,
          actor: attachment.uploadedBy === 'system' ? 'System' : attachment.uploadedBy,
          type: 'attachment',
        });
      });
    }

    // Sort all events by timestamp (newest first)
    events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    return events;
  };

  // Fetch incident data on mount
  useEffect(() => {
    const fetchIncident = async () => {
      if (!id) { return; }

      try {
        setLoading(true);
        const data = await incidentsService.getIncidentById(id);
        setIncident(data);
        // Populate linked investigations from enriched response
        const linked = (data as unknown as Record<string, unknown>).linkedInvestigations as Array<{ id: string; caseNumber: string; title: string; status: string }> | undefined;
        setLinkedInvestigations(linked ?? []);
        // Initialize form with current values
        setEditForm({
          description: data.description,
          location: data.location ?? '',
          category: data.category ?? '',
          severity: data.severity,
          affectedSystems: data.affectedSystems ?? [],
          tags: data.tags ?? [],
          assigneeId: data.assignedTo ?? '',
          occurredAt: data.occurredAt ? toDateTimeLocalValue(new Date(data.occurredAt)) : '',
          incidentNumber: data.incidentNumber ?? '',
          supervisorId: data.supervisorId ?? '',
          supervisorName: data.supervisorName ?? '',
          supervisorEmail: data.supervisorEmail ?? '',
        });
        setError(null);
      } catch (err) {
        console.error('Failed to fetch incident:', err);
        setError('Failed to load incident details');
      } finally {
        setLoading(false);
      }
    };

    fetchIncident();
  }, [id]);

  // Set breadcrumb override when incident is loaded
  useEffect(() => {
    if (incident?.incidentNumber) {
      setOverride(location.pathname, incident.incidentNumber);
    }
    return () => {
      clearOverride(location.pathname);
    };
  }, [incident?.incidentNumber, location.pathname, setOverride, clearOverride]);

  // Fetch assignee user data when incident loads
  useEffect(() => {
    const fetchAssigneeUser = async () => {
      // If incident has assignee object from backend, use that
      if (incident?.assignee) {
        setAssigneeUser(null); // Clear since we'll use incident.assignee
        return;
      }

      // If incident has assignedTo ID, fetch the user
      if (incident?.assignedTo) {
        try {
          const user = await usersService.getUserById(incident.assignedTo);
          setAssigneeUser(user);
        } catch (err) {
          console.error('Failed to fetch assignee user:', err);
          setAssigneeUser(null); // Clear on error
        }
      } else {
        setAssigneeUser(null);
      }
    };

    fetchAssigneeUser();
  }, [incident?.assignedTo, incident?.assignee]);

  // Fetch users when entering edit mode
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isEditing) { return; }

      try {
        setLoadingUsers(true);
        const response = await usersService.getUsers({ limit: 100, isActive: true });
        setAvailableUsers(response.users);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        toast.error('Failed to load users list');
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [isEditing]);

  // Keyboard shortcut: Cmd/Ctrl+Shift+I → open Create Investigation modal (VIS-23)
  useEffect(() => {
    if (!canCreateInvestigation) { return; }
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
        if (investigationCreationBlocked) { return; }
        e.preventDefault();
        setShowCreateInvestigationModal(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canCreateInvestigation, investigationCreationBlocked]);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  // Helper function to get assignee display name
  const getAssigneeDisplayName = () => {
    if (!incident) { return 'Unassigned'; }

    // First try to use the assignee object if it exists (from backend)
    if (incident.assignee) {
      return `${incident.assignee.firstName} ${incident.assignee.lastName}`;
    }

    // Use the fetched assignee user data
    if (assigneeUser) {
      return assigneeUser.fullName;
    }

    // If we have an assignedTo ID and users are loaded, look up the user
    if (incident.assignedTo && availableUsers.length > 0) {
      const user = availableUsers.find(u => u.id === incident.assignedTo);
      if (user) {
        return user.fullName;
      }
    }

    // Fallback: show "Unassigned" if no assignedTo, otherwise show loading indicator
    if (!incident.assignedTo) {
      return 'Unassigned';
    }

    // If we have an ID but haven't loaded the user yet, show a placeholder
    return 'Loading...';
  };

  const handleEditClick = () => {
    if (incident) {
      // Reset form to current incident values
      setEditForm({
        description: incident.description,
        location: incident.location ?? '',
        category: incident.category ?? '',
        severity: incident.severity,
        affectedSystems: incident.affectedSystems ?? [],
        tags: incident.tags ?? [],
        assigneeId: incident.assignedTo ?? '',
        occurredAt: incident.occurredAt ? toDateTimeLocalValue(new Date(incident.occurredAt)) : '',
        incidentNumber: incident.incidentNumber ?? '',
        supervisorId: incident.supervisorId ?? '',
        supervisorName: incident.supervisorName ?? '',
        supervisorEmail: incident.supervisorEmail ?? '',
      });
      setSaveError(null);
      setSaveSuccess(false);
      setIsEditing(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError(null);
    setSaveSuccess(false);
    setNewSystem('');
    setNewTag('');
  };

  const handleSaveEdit = async () => {
    if (!id || !incident) { return; }

    try {
      setIsSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const updateData: UpdateIncidentData = {
        description: editForm.description,
        location: editForm.location ?? undefined,
        category: editForm.category ?? undefined,
        severity: editForm.severity,
        affectedSystems: editForm.affectedSystems.length > 0 ? editForm.affectedSystems : undefined,
        tags: editForm.tags.length > 0 ? editForm.tags : undefined,
        occurredAt: editForm.occurredAt || null,
      };

      // Add assignee if it has changed (normalize unassigned to '' on both sides
      // so an unassigned incident doesn't spuriously report an assignee change)
      if (editForm.assigneeId !== (incident.assignedTo ?? '')) {
        updateData.assigneeId = editForm.assigneeId ?? undefined;
        if (editForm.assigneeId) {
          updateData.assignedAt = new Date().toISOString();
        }
      }

      // Add supervisor if it has changed
      if (editForm.supervisorId !== (incident.supervisorId ?? '')) {
        if (editForm.supervisorId) {
          updateData.supervisor = {
            id: editForm.supervisorId,
            name: editForm.supervisorName,
            email: editForm.supervisorEmail,
          };
        } else {
          updateData.supervisor = null;
        }
      }

      // Add incident number if it has changed (admin+ only)
      if (editForm.incidentNumber && editForm.incidentNumber !== incident.incidentNumber) {
        // Validate format client-side before sending
        if (!/^INC-\d{7}$/.test(editForm.incidentNumber)) {
          setSaveError('Incident number must follow format INC-NNNNNNN (e.g., INC-0000042)');
          setIsSaving(false);
          return;
        }
        updateData.incidentNumber = editForm.incidentNumber;
      }

      const updatedIncident = await incidentsService.updateIncident(id, updateData);
      setIncident(updatedIncident);
      setIsEditing(false);
      setSaveSuccess(true);
      setNewSystem('');
      setNewTag('');

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: unknown) {
      console.error('Failed to update incident:', err);
      // Handle 409 conflict (incident number already in use)
      const apiError = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (apiError?.response?.status === 409) {
        setSaveError(apiError.response.data?.error?.message ?? 'Incident number is already in use.');
      } else if (apiError?.response?.status === 403) {
        setSaveError(apiError.response.data?.error?.message ?? 'You do not have permission to perform this action.');
      } else {
        setSaveError('Failed to save changes. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSystem = () => {
    if (newSystem.trim() && !editForm.affectedSystems.includes(newSystem.trim())) {
      setEditForm(prev => ({
        ...prev,
        affectedSystems: [...prev.affectedSystems, newSystem.trim()],
      }));
      setNewSystem('');
    }
  };

  const handleRemoveSystem = (system: string) => {
    setEditForm(prev => ({
      ...prev,
      affectedSystems: prev.affectedSystems.filter(s => s !== system),
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editForm.tags.includes(newTag.trim())) {
      setEditForm(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setEditForm(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag),
    }));
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !id || !incident) { return; }

    try {
      setIsAddingComment(true);

      // Add the comment via API
      const addedComment = await incidentsService.addComment(id, newComment.trim());

      // Optimistically update the incident with the new comment
      setIncident(prev => {
        if (!prev) { return prev; }
        return {
          ...prev,
          comments: [addedComment, ...(prev.comments ?? [])],
        };
      });

      setNewComment('');
    } catch (err) {
      console.error('Failed to add comment:', err);
      // Could show error message to user here
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleAddFileClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) { return; }

    // Validate file size
    if (file.size > FILE_SIZE_LIMITS.DOCUMENT_MAX_SIZE_BYTES) {
      setUploadError(FILE_UPLOAD_ERRORS.FILE_TOO_LARGE_DOCUMENT);
      return;
    }

    try {
      setIsUploadingFile(true);
      setUploadError(null);

      const newAttachment = await incidentsService.uploadAttachment(id, file);

      // Update incident with new attachment
      setIncident(prev => {
        if (!prev) { return prev; }
        return {
          ...prev,
          attachments: [...(prev.attachments ?? []), newAttachment],
        };
      });

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: unknown) {
      console.error('Failed to upload file:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    // eslint-disable-next-line no-alert
    if (!confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await incidentsService.deleteAttachment(attachmentId);

      // Update incident by removing the attachment
      setIncident(prev => {
        if (!prev) { return prev; }
        return {
          ...prev,
          attachments: prev.attachments?.filter(a => a.id !== attachmentId),
        };
      });
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      // eslint-disable-next-line no-alert
      alert('Failed to delete attachment. Please try again.');
    }
  };

  const handleDownloadAttachment = async (attachmentId: string, filename: string) => {
    try {
      const url = incidentsService.getAttachmentDownloadUrl(attachmentId);
      await apiClient.downloadFile(url, filename);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      toast.error('Failed to download attachment');
    }
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) { return PhotoIcon; }
    return DocumentTextIcon;
  };

  // Quick Actions Handlers
  const handleMarkResolved = async () => {
    if (!id || !incident) { return; }

    try {
      const updatedIncident = await incidentsService.updateIncident(id, {
        status: 'RESOLVED',
        resolvedAt: new Date().toISOString(),
      });
      setIncident(updatedIncident);
      setShowResolveConfirm(false);
      toast.success('Incident marked as resolved');
    } catch (err) {
      console.error('Failed to resolve incident:', err);
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to mark incident as resolved',
      );
    }
  };

  const handleReassign = async (userId: string) => {
    if (!id || !incident) { return; }

    try {
      const updatedIncident = await incidentsService.updateIncident(id, {
        assigneeId: userId,
        assignedAt: new Date().toISOString(),
      });
      setIncident(updatedIncident);
    } catch (err) {
      console.error('Failed to reassign incident:', err);
      throw err; // Re-throw to let modal handle the error
    }
  };

  const handleChangePriority = async (priority: string) => {
    if (!id || !incident) { return; }

    try {
      const updatedIncident = await incidentsService.updateIncident(id, {
        priority: priority as 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT',
      });
      setIncident(updatedIncident);
    } catch (err) {
      console.error('Failed to change priority:', err);
      throw err; // Re-throw to let modal handle the error
    }
  };

  const handleEscalate = async (data: EscalateData) => {
    if (!id || !incident) { return; }

    try {
      // Prepare update data
      const updateData: UpdateIncidentData = {};

      if (data.increaseSeverity && data.newSeverity) {
        updateData.severity = data.newSeverity as 'MINIMAL' | 'MODERATE' | 'HIGH';
      }

      if (data.setPriorityUrgent) {
        updateData.priority = 'URGENT';
      }

      // Update incident
      const updatedIncident = await incidentsService.updateIncident(id, updateData);

      // Add escalation comment
      const escalationComment = `🚨 INCIDENT ESCALATED\n\nReason: ${data.reason}`;
      const addedComment = await incidentsService.addComment(id, escalationComment);

      // Update local state with both incident updates and new comment
      setIncident({
        ...updatedIncident,
        comments: [addedComment, ...(updatedIncident.comments ?? [])],
      });
    } catch (err) {
      console.error('Failed to escalate incident:', err);
      throw err; // Re-throw to let modal handle the error
    }
  };

  // Closure Workflow Handlers
  const handleClosureError = (err: unknown) => {
    // ClosureFlowError carries the API error code directly (preferred).
    // Fall back to the legacy axios-shaped error path for any non-closure
    // failures that might surface here.
    const closureErr = err as { code?: string; message?: string };
    const apiError = err as { response?: { data?: { error?: { code?: string; message?: string } } } };
    const code = closureErr?.code ?? apiError?.response?.data?.error?.code;
    const fallbackMessages: Record<string, string> = {
      APPROVAL_REQUIRED: "Closure requires supervisor approval. Click 'Request Closure' instead.",
      SUPERVISOR_REQUIRED: 'Assign a supervisor to this incident before requesting closure.',
      NOTE_REQUIRED: 'A rejection note is required.',
      INVALID_STATE_TRANSITION: 'This action is not available in the current incident state.',
      SELF_APPROVAL_FORBIDDEN: 'You cannot act as both requester and supervisor on the same incident.',
      SUPERVISOR_MISMATCH: 'Only the assigned supervisor can approve this closure.',
      NOT_REQUESTER: 'Only the user who requested closure can withdraw it.',
      INCIDENT_CLOSED: 'This incident is closed and cannot be modified.',
      INSUFFICIENT_PERMISSIONS: 'You do not have permission to perform this action.',
    };
    const msg =
      (code && fallbackMessages[code]) ??
      closureErr?.message ??
      apiError?.response?.data?.error?.message ??
      'Action failed';
    toast.error(msg);
  };

  const handleRequestClosure = async (note?: string) => {
    if (!id) { return; }
    try {
      const updated = await incidentsService.requestClosure(id, note);
      setIncident(updated);
      toast.success('Closure request submitted to your supervisor');
    } catch (err) {
      handleClosureError(err);
      throw err;
    }
  };

  const handleApproveClosure = async (note?: string) => {
    if (!id) { return; }
    try {
      const updated = await incidentsService.approveClosure(id, note);
      setIncident(updated);
      toast.success('Incident closed');
    } catch (err) {
      handleClosureError(err);
      throw err;
    }
  };

  const handleRejectClosure = async (note: string) => {
    if (!id) { return; }
    try {
      const updated = await incidentsService.rejectClosure(id, note);
      setIncident(updated);
      toast.success('Closure rejected; incident returned to In Progress');
    } catch (err) {
      handleClosureError(err);
      throw err;
    }
  };

  const handleWithdrawClosure = async () => {
    if (!id) { return; }
    try {
      const updated = await incidentsService.withdrawClosure(id);
      setIncident(updated);
      toast.success('Closure request withdrawn');
    } catch (err) {
      handleClosureError(err);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: 'easeOut' },
    },
  };

  // Show loading state
  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-lg text-secondary-600 dark:text-secondary-400">
              Loading incident details...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state
  if (error ?? !incident) {
    return (
      <div className="p-4 sm:p-6 space-y-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-400 dark:text-danger-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-2">
              Error Loading Incident
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400 mb-4">
              {error ?? 'Incident not found'}
            </p>
            <Button onClick={() => navigate('/security/incidents')} variant="outline">
              Back to Incidents
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="p-4 sm:p-6 space-y-6"
    >
      {/* Success/Error Messages */}
      {saveSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-success-100 dark:bg-success-900/20 border border-success-200 dark:border-success-800 text-success-800 dark:text-success-300 px-4 py-3 rounded-lg"
        >
          Changes saved successfully!
        </motion.div>
      )}
      {saveError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-danger-100 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 text-danger-800 dark:text-danger-300 px-4 py-3 rounded-lg"
        >
          {saveError}
        </motion.div>
      )}

      {/* Header */}
      <motion.div variants={itemVariants}>
        <div className="flex items-center space-x-4 mb-6">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate('/security/incidents')}
            className="flex-shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-3 mb-2">
              <h1 className="text-2xl font-bold text-secondary-900 dark:text-white truncate">
                {incident.title}
              </h1>
              {isEditing && canEditIncidentNumber ? (
                <input
                  type="text"
                  value={editForm.incidentNumber}
                  onChange={(e) => setEditForm({ ...editForm, incidentNumber: e.target.value.toUpperCase() })}
                  className={cn(
                    'text-sm font-mono px-2 py-1 border rounded bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white w-36 focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                    editForm.incidentNumber && !/^INC-\d{7}$/.test(editForm.incidentNumber)
                      ? 'border-danger-400 dark:border-danger-600'
                      : 'border-secondary-300 dark:border-secondary-600'
                  )}
                  placeholder="INC-0000000"
                />
              ) : (
                <span className="text-sm font-mono text-secondary-500 dark:text-secondary-400">
                  {incident.incidentNumber}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={cn('px-3 py-1 text-xs font-medium rounded-full border', severityColors[incident.severity.toLowerCase() as keyof typeof severityColors])}>
                {incident.severity}
              </span>
              <span className={cn('px-3 py-1 text-xs font-medium rounded-full', statusColors[incident.status.toLowerCase().replace('_', '_') as keyof typeof statusColors] || statusColors.open)}>
                {incident.status.replace('_', ' ')}
              </span>
              {incident.category && (
                <span className="px-3 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full">
                  {incident.category}
                </span>
              )}
            </div>
          </div>
          {!isEditing ? (
            <div className="flex items-center space-x-2">
              {canCreateInvestigation && (
                <Button
                  onClick={() => setShowCreateInvestigationModal(true)}
                  disabled={investigationCreationBlocked}
                  title={
                    investigationCreationBlocked
                      ? 'Create Investigation is not available on closed/cancelled/resolved incidents'
                      : 'Create a linked investigation from this incident'
                  }
                >
                  Create Investigation
                </Button>
              )}
              <Button onClick={handleEditClick}>
                <PencilIcon className="w-4 h-4 mr-2" />
                Edit
              </Button>
            </div>
          ) : (
            <div className="flex space-x-2">
              <Button onClick={handleSaveEdit} loading={isSaving} disabled={isSaving}>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Save
              </Button>
              <Button onClick={handleCancelEdit} variant="outline" disabled={isSaving}>
                <XCircleIcon className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </div>
          )}
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <DocumentTextIcon className="w-5 h-5" />
                  <span>Description</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <RichTextEditor
                    value={editForm.description}
                    onChange={(html) => setEditForm({ ...editForm, description: html })}
                    placeholder="Enter incident description..."
                    minHeight={200}
                  />
                ) : (
                  <RichTextDisplay
                    content={incident.description}
                    className="text-secondary-700 dark:text-secondary-300 leading-relaxed"
                    emptyMessage="No description provided."
                  />
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Impact & Affected Systems */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Impact Assessment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white mb-2">Severity Level</h4>
                  {isEditing ? (
                    <select
                      value={editForm.severity}
                      onChange={(e) => setEditForm({ ...editForm, severity: e.target.value as 'MINIMAL' | 'MODERATE' | 'HIGH' })}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="MINIMAL">Minimal</option>
                      <option value="MODERATE">Moderate</option>
                      <option value="HIGH">High</option>
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <span className={cn('px-3 py-1 text-sm font-medium rounded-full border', severityColors[incident.severity.toLowerCase() as keyof typeof severityColors])}>
                        {incident.severity}
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white mb-2">Affected Systems</h4>
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newSystem}
                          onChange={(e) => setNewSystem(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddSystem();
                            }
                          }}
                          placeholder="Enter system name..."
                          className="flex-1 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <Button
                          type="button"
                          onClick={handleAddSystem}
                          variant="outline"
                          size="sm"
                          disabled={!newSystem.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      {editForm.affectedSystems.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {editForm.affectedSystems.map((system) => (
                            <span
                              key={system}
                              className="px-3 py-1 text-sm bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-full flex items-center space-x-2"
                            >
                              <span>{system}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveSystem(system)}
                                className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : incident.affectedSystems && incident.affectedSystems.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {incident.affectedSystems.map((system) => (
                        <span
                          key={system}
                          className="px-3 py-1 text-sm bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-300 rounded-full"
                        >
                          {system}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">No systems specified</p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-secondary-900 dark:text-white mb-2">Tags</h4>
                  {isEditing ? (
                    <div className="space-y-2">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddTag();
                            }
                          }}
                          placeholder="Enter tag..."
                          className="flex-1 px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                        <Button
                          type="button"
                          onClick={handleAddTag}
                          variant="outline"
                          size="sm"
                          disabled={!newTag.trim()}
                        >
                          Add
                        </Button>
                      </div>
                      {editForm.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {editForm.tags.map((tag) => (
                            <span
                              key={tag}
                              className="px-3 py-1 text-sm bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full flex items-center space-x-2"
                            >
                              <span>#{tag}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveTag(tag)}
                                className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-200"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : incident.tags && incident.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {incident.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-3 py-1 text-sm bg-secondary-100 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-300 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400">No tags specified</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Linked Subjects */}
          <motion.div variants={itemVariants}>
            <LinkedSubjectsPanel caseId={id} caseType="INCIDENT" />
          </motion.div>

          {/* Comments */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ChatBubbleLeftIcon className="w-5 h-5" />
                  <span>Investigative Updates</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {incident.comments && incident.comments.length > 0 ? (
                  incident.comments.map((comment) => (
                    <div key={comment.id} className="border-l-4 border-primary-200 dark:border-primary-800 pl-4 py-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-secondary-900 dark:text-white">
                          {comment.userId === 'system' ? 'System' : `User ${comment.userId.substring(0, 8)}`}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-400">
                          {incidentsService.formatTimestamp(comment.createdAt)}
                        </span>
                      </div>
                      <p className="text-secondary-700 dark:text-secondary-300 text-sm">
                        {comment.comment}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                    <ChatBubbleLeftIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No comments yet. Be the first to add one!</p>
                  </div>
                )}

                {/* Add Comment */}
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or update..."
                    className="w-full p-3 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                    rows={3}
                  />
                  <div className="flex justify-end mt-2">
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || isAddingComment}
                      loading={isAddingComment}
                    >
                      Add Comment
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Attachments */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <PaperClipIcon className="w-5 h-5" />
                    <span>Attachments</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAddFileClick}
                    disabled={isUploadingFile}
                    loading={isUploadingFile}
                  >
                    Add File
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  accept={DOCUMENT_FILE_EXTENSIONS}
                />

                {/* Upload error */}
                {uploadError && (
                  <div className="mb-3 p-3 bg-danger-100 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 text-danger-800 dark:text-danger-300 rounded-lg text-sm">
                    {uploadError}
                  </div>
                )}

                {incident.attachments && incident.attachments.length > 0 ? (
                  <div className="space-y-3">
                    {incident.attachments.map((attachment) => {
                      const FileIcon = getFileIcon(attachment.mimeType);
                      return (
                        <div
                          key={attachment.id}
                          className="flex items-center justify-between p-3 border border-secondary-200 dark:border-secondary-700 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors"
                        >
                          <div className="flex items-center space-x-3 flex-1 min-w-0">
                            <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-secondary-900 dark:text-white truncate">
                                {attachment.originalFilename}
                              </p>
                              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                                {incidentsService.formatFileSize(attachment.fileSize)} • {incidentsService.getFileTypeLabel(attachment.mimeType)} • {incidentsService.formatTimestamp(attachment.uploadedAt)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadAttachment(attachment.id, attachment.originalFilename)}
                              title="Download"
                            >
                              <ArrowDownTrayIcon className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAttachment(attachment.id)}
                              className="text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300"
                              title="Delete"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-secondary-500 dark:text-secondary-400">
                    <PaperClipIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No attachments yet. Click &quot;Add File&quot; to upload.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Closure Workflow */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Closure</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {incidentActive && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => setShowRequestClosureModal(true)}
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Request Closure
                  </Button>
                )}
                {incidentPending && isSupervisor && (
                  <>
                    <Button
                      className="w-full justify-start"
                      onClick={() => setShowApproveClosureModal(true)}
                    >
                      <CheckCircleIcon className="w-4 h-4 mr-2" />
                      Approve Closure
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-danger-700 border-danger-300"
                      onClick={() => setShowRejectClosureModal(true)}
                    >
                      <XCircleIcon className="w-4 h-4 mr-2" />
                      Reject Closure
                    </Button>
                  </>
                )}
                {incidentPending && isRequester && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleWithdrawClosure}
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-2" />
                    Withdraw Closure Request
                  </Button>
                )}
                {incidentPending && !isSupervisor && !isRequester && (
                  <div className="text-sm text-warning-800 bg-warning-50 dark:bg-warning-900/20 dark:text-warning-300 px-3 py-2 rounded-lg">
                    Awaiting closure approval from {incident.supervisorName ?? 'supervisor'}.
                    {incident.closureRequestedAt && (
                      <> Requested on {formatTimestamp(incident.closureRequestedAt)}.</>
                    )}
                  </div>
                )}
                {incidentClosed && (
                  <p className="text-sm text-secondary-500 dark:text-secondary-400">
                    Incident is {status.toLowerCase()}.
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Incident Details */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Incident Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <UserIcon className="w-4 h-4" />
                    <span className="text-sm">Reporter</span>
                  </div>
                  <p className="font-medium text-secondary-900 dark:text-white">
                    {incident.reporterName}
                  </p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <UserIcon className="w-4 h-4" />
                    <span className="text-sm">Assignee</span>
                  </div>
                  {isEditing ? (
                    <select
                      value={editForm.assigneeId ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, assigneeId: e.target.value ?? undefined })}
                      disabled={loadingUsers}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                    >
                      <option value="">Unassigned</option>
                      {loadingUsers ? (
                        <option disabled>Loading users...</option>
                      ) : (
                        availableUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.fullName}
                          </option>
                        ))
                      )}
                    </select>
                  ) : (
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {getAssigneeDisplayName()}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <UserIcon className="w-4 h-4" />
                    <span className="text-sm">Supervisor</span>
                  </div>
                  {isEditing ? (
                    <UserPicker
                      label=""
                      value={editForm.supervisorId ?? ''}
                      roleFilter={['SECURITY_MANAGER', 'ADMIN', 'SUPER_ADMIN']}
                      disabled={incident.status === 'CLOSED'}
                      onChange={(picked) => {
                        setEditForm({
                          ...editForm,
                          supervisorId: picked?.id ?? '',
                          supervisorName: picked?.name ?? '',
                          supervisorEmail: picked?.email ?? '',
                        });
                      }}
                    />
                  ) : (
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {incident.supervisorName ?? 'Unassigned'}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <MapPinIcon className="w-4 h-4" />
                    <span className="text-sm">Location</span>
                  </div>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.location}
                      onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Enter location..."
                    />
                  ) : (
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {incident.location ?? 'Not specified'}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <DocumentTextIcon className="w-4 h-4" />
                    <span className="text-sm">Category</span>
                  </div>
                  {isEditing ? (
                    <select
                      value={editForm.category}
                      onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                      className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">Select category...</option>
                      {SECURITY_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {incident.category ?? 'Not specified'}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="text-sm">Occurred Date</span>
                  </div>
                  {isEditing ? (
                    <DateTimeInput
                      value={editForm.occurredAt}
                      onChange={(value) => setEditForm({ ...editForm, occurredAt: value })}
                      max={toDateTimeLocalValue(new Date())}
                    />
                  ) : (
                    <p className="font-medium text-secondary-900 dark:text-white">
                      {incident.occurredAt ? formatTimestamp(incident.occurredAt) : 'Not specified'}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="text-sm">Created</span>
                  </div>
                  <p className="font-medium text-secondary-900 dark:text-white">
                    {formatTimestamp(incident.createdAt)}
                  </p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-secondary-600 dark:text-secondary-400 mb-1">
                    <ClockIcon className="w-4 h-4" />
                    <span className="text-sm">Last Updated</span>
                  </div>
                  <p className="font-medium text-secondary-900 dark:text-white">
                    {formatTimestamp(incident.updatedAt)}
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Linked Investigations */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Linked Investigations</CardTitle>
                  <button
                    onClick={() => setShowLinkInvestigationModal(true)}
                    className="text-xs px-2 py-1 text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded transition-colors"
                  >
                    + Link
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                {linkedInvestigations.length === 0 ? (
                  <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
                    No linked investigations
                  </p>
                ) : (
                  <div className="space-y-2">
                    {linkedInvestigations.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-2 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors group"
                      >
                        <Link
                          to={`/security/investigations/${inv.id}`}
                          className="flex-1 min-w-0"
                        >
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-xs font-mono text-primary-600 dark:text-primary-400">
                              {inv.caseNumber}
                            </span>
                            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                              inv.status === 'ACTIVE' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                              inv.status === 'CLOSED' ? 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300' :
                              inv.status === 'SUSPENDED' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                              'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                            }`}>
                              {inv.status}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-secondary-900 dark:text-white truncate">
                            {inv.title}
                          </p>
                        </Link>
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await incidentsService.unlinkInvestigation(id, inv.id);
                              setLinkedInvestigations((prev) => prev.filter((i) => i.id !== inv.id));
                              toast.success('Investigation unlinked');
                            } catch {
                              toast.error('Failed to unlink investigation');
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1 text-secondary-400 hover:text-danger-500 transition-all flex-shrink-0 ml-2"
                          title="Unlink investigation"
                        >
                          <XMarkSmallIcon className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Timeline */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {incident && buildTimeline(incident).map((event, index, array) => {
                    const Icon = timelineIcons[event.type];
                    return (
                      <div key={event.id} className="relative">
                        {index < array.length - 1 && (
                          <div className="absolute left-4 top-8 w-0.5 h-12 bg-secondary-200 dark:bg-secondary-700" />
                        )}
                        <div className="flex items-start space-x-3">
                          <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-secondary-900 dark:text-white text-sm">
                              {event.action}
                            </p>
                            <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">
                              {event.details}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                              {formatTimestamp(event.timestamp)} • {event.actor}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {incident && buildTimeline(incident).length === 0 && (
                    <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center py-4">
                      No timeline events yet
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowResolveConfirm(true)}
                  disabled={!canResolve}
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  Mark as Resolved
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowReassignModal(true)}
                >
                  <UserIcon className="w-4 h-4 mr-2" />
                  Reassign
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowPriorityModal(true)}
                >
                  <ClockIcon className="w-4 h-4 mr-2" />
                  Change Priority
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => setShowEscalateModal(true)}
                >
                  <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                  Escalate
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>

      {/* Quick Actions Modals */}
      <ConfirmationDialog
        isOpen={showResolveConfirm}
        onClose={() => setShowResolveConfirm(false)}
        onConfirm={handleMarkResolved}
        title="Mark as Resolved"
        message="Are you sure you want to mark this incident as resolved? This will update the incident status and record the resolution time."
        confirmText="Mark as Resolved"
        cancelText="Cancel"
        variant="default"
      />

      <ReassignModal
        isOpen={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        currentAssignee={incident?.assignee}
        onReassign={handleReassign}
      />

      <ChangePriorityModal
        isOpen={showPriorityModal}
        onClose={() => setShowPriorityModal(false)}
        currentPriority={incident?.priority || 'MEDIUM'}
        onChangePriority={handleChangePriority}
      />

      <EscalateModal
        isOpen={showEscalateModal}
        onClose={() => setShowEscalateModal(false)}
        currentSeverity={incident?.severity || 'MEDIUM'}
        currentPriority={incident?.priority || 'MEDIUM'}
        onEscalate={handleEscalate}
      />

      <LinkSearchModal
        isOpen={showLinkInvestigationModal}
        onClose={() => setShowLinkInvestigationModal(false)}
        type="investigation"
        excludeIds={linkedInvestigations.map((i) => i.id)}
        onSelect={async (investigationId) => {
          await incidentsService.linkInvestigation(id, investigationId);
          // Re-fetch to get the updated linked investigations with summaries
          const data = await incidentsService.getIncidentById(id);
          const linked = (data as unknown as Record<string, unknown>).linkedInvestigations as Array<{ id: string; caseNumber: string; title: string; status: string }> | undefined;
          setLinkedInvestigations(linked ?? []);
          toast.success('Investigation linked');
        }}
      />

      {incident && (
        <CreateInvestigationFromIncidentModal
          open={showCreateInvestigationModal}
          incident={incident}
          onClose={() => setShowCreateInvestigationModal(false)}
          onCreated={(inv) => {
            setShowCreateInvestigationModal(false);
            setLinkedInvestigations((prev) => [
              { id: inv.id, caseNumber: inv.caseNumber, title: inv.title, status: inv.status },
              ...prev,
            ]);
          }}
        />
      )}

      <RequestClosureModal
        isOpen={showRequestClosureModal}
        onClose={() => setShowRequestClosureModal(false)}
        onSubmit={handleRequestClosure}
      />
      <ApproveClosureModal
        isOpen={showApproveClosureModal}
        onClose={() => setShowApproveClosureModal(false)}
        onSubmit={handleApproveClosure}
      />
      <RejectClosureModal
        isOpen={showRejectClosureModal}
        onClose={() => setShowRejectClosureModal(false)}
        onSubmit={handleRejectClosure}
      />
    </motion.div>
  );
}